import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useSalaryBandApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import CheckboxInput from '../../components/ui/CheckboxInput';

const Container = styled.div``;

// Ücret Bantları (spec 2026-10-27 §7.1). Varsayılan görünüm: her (kademe, aile, para birimi) için bugün geçerli tek satır.
export default function SalaryBandsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [currentOnly, setCurrentOnly] = useState(true);
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [] });
  const [deleting, setDeleting] = useState(false);

  const { objectSchema, fetching, columns: baseColumns, data, pagination, create, update, _delete } = useSalaryBandApi(queryParams, currentOnly);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({ ...row, validFrom: row.validFrom ? String(row.validFrom).slice(0, 10) : '' });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow ? await update(selectedRow.id, formData) : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [] });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const actionOptions = useMemo(() => {
    if (!canWrite('EM')) return [];
    return [
      { type: 'edit', label: t('salaryBand.edit'), onClick: handleEdit },
      { type: 'divider' },
      { type: 'delete', label: t('salaryBand.delete'), onClick: (row) => setDeleteConfirm({ isOpen: true, ids: [row.id] }) },
    ];
  }, [t, canWrite, handleEdit]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => <OptionsMenu actions={actionOptions} row={data[tableMeta.rowIndex]} />,
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('salaryBand.edit') : t('salaryBand.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('salaryBand.deleteTitle')}
        message={t('salaryBand.deleteMessage')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [] })}
        loading={deleting}
      />

      <DataTable
        title={t('salaryBand.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canWrite('EM')}
        onNewButtonClick={handleNewButtonClick}
        filterList={filterList}
        onFilterConfirm={setFilterList}
        onTableChanged={handleDataStateChange}
        toolbarActions={(
          <CheckboxInput
            label={t('salaryBand.currentOnly')}
            checked={currentOnly}
            onChange={(value) => setCurrentOnly(!!value)}
          />
        )}
      />
    </Container>
  );
}
